import { useEffect } from "react";
import type { CityConfig, CityFAQ } from "../types";

export default function useCitySEO(config: CityConfig) {
  useEffect(() => {
    document.title = config.title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = `https://fastpassportcenter.com${config.route}/`;

    setLink("canonical", pageUrl);
    setMeta("description", config.metaDescription);
    setMeta("geo.placename", config.geoPlacename);
    setMeta("geo.region", `US-${config.stateAbbr}`);
    setMeta("geo.position", config.geoPosition);
    setMeta("ICBM", config.geoPosition.replace(";", ", "));
    if (config.lastModified) {
      setMeta("last-modified", config.lastModified);
    }

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: config.statePageName, item: `https://fastpassportcenter.com${config.statePageRoute}/` },
          { "@type": "ListItem", position: 3, name: `${config.city} Passport Services`, item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: `Fast Passport Center does not have a physical office in ${config.city} — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for ${config.city} and ${config.state} travelers.`,
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: config.city,
          containedInPlace: {
            "@type": "State",
            name: config.state,
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...config.servicesSchema.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: config.city,
        },
        description: s.description,
      })),
      {
        "@type": "HowTo",
        name: `How to Get an Expedited Passport in ${config.city}`,
        description: `Step-by-step guide to getting an emergency or expedited passport in ${config.city} through Fast Passport Center. As fast as 24 hours with no agency visit required.`,
        totalTime: "PT10M",
        supply: [
          { "@type": "HowToSupply", name: "Proof of citizenship (birth certificate or naturalization certificate)" },
          { "@type": "HowToSupply", name: "Government-issued photo ID" },
          { "@type": "HowToSupply", name: "Two passport photos" },
          { "@type": "HowToSupply", name: "Payment for government fees" },
        ],
        tool: [
          { "@type": "HowToTool", name: "Overnight shipping label" },
        ],
        step: config.steps.map((step, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: step.title,
          text: step.description,
          url: `https://fastpassportcenter.com${config.route}/#how-it-works`,
        })),
      },
      {
        "@type": "FAQPage",
        mainEntity: config.faqs.map((faq: CityFAQ) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, [config]);
}